import { requestLoginModal } from '@corefront/redux-aba';
import { useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { notify } from '@casinofe/react-toast';
import { getAPI } from '../../services/API';
import { queryClient } from '../../services/queryClient';
import useRequestConfig from './useRequestConfig';
import { useIsFavoriteGame } from '../../components/Button/FavoriteButton/hooks';
import { isLoggedInSelector } from '../../redux/reducers/app/params';
import { sdkErrorHandler } from '../../utils';
import { DEFAULT_LOCIZE_NAMESPACE } from '../../constants';

export const QUERY_KEY_FAVOURITE_GAMES = 'favourite-games';

const FAVOURITE_GAMES_QUERY_OPTIONS = {
  cacheTime: 0,
  // because of native channels
  refetchOnWindowFocus: 'always',
};

export const useQueryFavouriteGames = (queryOptions = {}, productType) => {
  const requestConfig = useRequestConfig();

  const fetch = ({ signal }) =>
    getAPI()
      .games.getFavourites({ signal, productType, ...requestConfig })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          productType,
          source: `SDK API games.getFavourites`,
        })
      );

  return useQuery([QUERY_KEY_FAVOURITE_GAMES, productType], fetch, {
    ...FAVOURITE_GAMES_QUERY_OPTIONS,
    ...queryOptions,
  });
};

export const useMutationAddGameToFavourite = (
  gameId,
  queryOptions = {},
  productType
) => {
  const requestConfig = useRequestConfig();

  const fetch = () =>
    getAPI()
      .games.addFavourite(gameId, {
        productType,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          gameId,
          queryOptions,
          productType,
          source: `SDK API games.addFavourite`,
        })
      );

  const onSuccess = useCallback(
    async (data) => {
      await queryClient.refetchQueries(QUERY_KEY_FAVOURITE_GAMES);
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess?.(data);
      }
    },
    [queryOptions]
  );

  return useMutation(fetch, { ...queryOptions, onSuccess });
};

export const useMutationRemoveGameFromFavourite = (
  gameId,
  queryOptions = {},
  productType
) => {
  const requestConfig = useRequestConfig();

  const fetch = () =>
    getAPI()
      .games.removeFavourite(gameId, {
        body: {}, // Be attention! Empty body is required. It needs for fix issue https://jira.pm.tech/browse/GAM-11430
        productType,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          gameId,
          queryOptions,
          productType,
          source: `SDK API games.removeFavourite`,
        })
      );

  const onSuccess = useCallback(async () => {
    await queryClient.refetchQueries(QUERY_KEY_FAVOURITE_GAMES);
    if (queryOptions.onSuccess) {
      queryOptions.onSuccess?.();
    }
  }, [queryOptions]);

  return useMutation(fetch, { ...queryOptions, onSuccess });
};

export const useToggleFavouriteGame = (gameId, productType, { onSuccess }) => {
  const { t } = useTranslation(DEFAULT_LOCIZE_NAMESPACE);

  const dispatch = useDispatch();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const isFavourite = useIsFavoriteGame(gameId, productType);

  const onError = useCallback(
    () =>
      notify.error({ title: t('Error'), message: t('Something went wrong') }),
    [t]
  );

  const { mutate: add } = useMutationAddGameToFavourite(
    gameId,
    {
      onError,
      onSuccess,
    },
    productType
  );
  const { mutate: remove } = useMutationRemoveGameFromFavourite(
    gameId,
    {
      onError,
      onSuccess,
    },
    productType
  );

  if (!isLoggedIn) {
    return () => dispatch(requestLoginModal());
  }

  return isFavourite ? remove : add;
};
